import { useEffect, useRef, useState } from 'react';

/**
 * Elegant editorial cursor: a small solid dot + a larger gradient ring that trails.
 * Both grow subtly when hovering interactive elements. Desktop only.
 */
export default function CustomCursor() {
  const dotRef = useRef(null);
  const ringRef = useRef(null);
  const targetRef = useRef({ x: -100, y: -100 });
  const dotPos = useRef({ x: -100, y: -100 });
  const ringPos = useRef({ x: -100, y: -100 });
  const [visible, setVisible] = useState(false);
  const [pressed, setPressed] = useState(false);
  const [hovering, setHovering] = useState(false);

  useEffect(() => {
    const isTouch = window.matchMedia('(pointer: coarse)').matches;
    const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (isTouch || reduced) {
      document.documentElement.classList.add('no-custom-cursor');
      return;
    }

    const onMove = (e) => {
      targetRef.current.x = e.clientX;
      targetRef.current.y = e.clientY;
      if (!visible) setVisible(true);
    };
    const onDown = () => setPressed(true);
    const onUp = () => setPressed(false);
    const onEnter = () => setVisible(true);
    const onLeave = () => setVisible(false);
    const onOver = (e) => {
      const el = e.target;
      const interactive = el && el.closest && el.closest('a, button, [role="button"], input, textarea, label, [data-cursor-hover]');
      setHovering(!!interactive);
    };
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mousedown', onDown);
    window.addEventListener('mouseup', onUp);
    document.addEventListener('mouseenter', onEnter);
    document.addEventListener('mouseleave', onLeave);
    window.addEventListener('mouseover', onOver, { passive: true });

    let raf;
    const loop = () => {
      dotPos.current.x += (targetRef.current.x - dotPos.current.x) * 0.42;
      dotPos.current.y += (targetRef.current.y - dotPos.current.y) * 0.42;
      ringPos.current.x += (targetRef.current.x - ringPos.current.x) * 0.14;
      ringPos.current.y += (targetRef.current.y - ringPos.current.y) * 0.14;
      if (dotRef.current) {
        dotRef.current.style.transform = `translate3d(${dotPos.current.x}px, ${dotPos.current.y}px, 0)`;
      }
      if (ringRef.current) {
        ringRef.current.style.transform = `translate3d(${ringPos.current.x}px, ${ringPos.current.y}px, 0)`;
      }
      raf = requestAnimationFrame(loop);
    };
    raf = requestAnimationFrame(loop);

    return () => {
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mousedown', onDown);
      window.removeEventListener('mouseup', onUp);
      document.removeEventListener('mouseenter', onEnter);
      document.removeEventListener('mouseleave', onLeave);
      window.removeEventListener('mouseover', onOver);
      cancelAnimationFrame(raf);
    };
  }, [visible]);

  const ringSize = hovering ? 64 : 40;

  return (
    <>
      {/* Trailing gradient ring */}
      <div
        ref={ringRef}
        aria-hidden="true"
        style={{
          position: 'fixed',
          top: 0,
          left: 0,
          width: ringSize,
          height: ringSize,
          marginLeft: -ringSize / 2,
          marginTop: -ringSize / 2,
          borderRadius: '50%',
          border: '1.5px solid transparent',
          backgroundImage:
            'linear-gradient(#10092E, #10092E), linear-gradient(135deg, #8948C3, #005CFF 55%, #40D3BE)',
          backgroundOrigin: 'border-box',
          backgroundClip: 'padding-box, border-box',
          pointerEvents: 'none',
          zIndex: 9998,
          opacity: visible ? (hovering ? 0.4 : 0.75) : 0,
          mixBlendMode: 'difference',
          transition: 'opacity .3s ease, width .25s ease, height .25s ease, margin .25s ease',
          willChange: 'transform',
          boxShadow: '0 8px 22px -6px rgba(137,72,195,0.35)',
        }}
      />
      {/* Center dot */}
      <div
        ref={dotRef}
        aria-hidden="true"
        style={{
          position: 'fixed',
          top: 0,
          left: 0,
          width: pressed ? 6 : 8,
          height: pressed ? 6 : 8,
          marginLeft: pressed ? -3 : -4,
          marginTop: pressed ? -3 : -4,
          borderRadius: '50%',
          background: 'linear-gradient(135deg, #8948C3, #005CFF 60%, #40D3BE)',
          boxShadow: '0 0 12px rgba(137,72,195,0.6), 0 0 24px rgba(0,92,255,0.35)',
          pointerEvents: 'none',
          zIndex: 9999,
          opacity: visible ? 1 : 0,
          transition: 'opacity .3s ease, width .2s ease, height .2s ease, margin .2s ease',
          willChange: 'transform',
        }}
      />
    </>
  );
}
